import React, { useCallback, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

/**
 * Reusable inline QR scanner box (rear camera).
 * onToken(token) fires when a valid Aura QR token is detected.
 */
export default function QrScanBox({ onToken, onError, size = 260, active = true, testId = 'qr-scan-box' }) {
  const scannerRef = useRef(null);
  const mountedRef = useRef(true);
  const regionId = useRef(`qr-region-${Math.random().toString(36).slice(2, 8)}`).current;

  const parseToken = useCallback((text) => {
    if (!text) return null;
    const h = text.match(/#([a-f0-9]{16,64})/i); if (h) return h[1];
    const p = text.match(/\/qr\/u\/([a-f0-9]{16,64})/i); if (p) return p[1];
    if (/^[a-f0-9]{16,64}$/i.test(text.trim())) return text.trim();
    return null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    const el = document.getElementById(regionId);
    if (!el) return undefined;
    let cancelled = false;
    const scanner = new Html5Qrcode(regionId, { verbose: false });
    scannerRef.current = scanner;

    const stop = async () => {
      try { await scanner.stop(); await scanner.clear(); } catch { /* ignore */ }
    };

    scanner
      .start({ facingMode: 'environment' }, { fps: 10, qrbox: size }, async (text) => {
        const token = parseToken(text);
        if (!token) return;
        await stop();
        if (!cancelled && mountedRef.current) onToken?.(token);
      }, () => {})
      .catch((e) => { onError?.(e?.message || 'Permiso de cámara denegado'); });

    return () => { cancelled = true; stop(); };
  }, [active, regionId, size, parseToken, onToken, onError]);

  return (
    <div
      id={regionId}
      data-testid={testId}
      className="w-full aspect-square rounded-2xl overflow-hidden bg-black border border-aura-cyan/40 shadow-neon-cyan"
    />
  );
}
